import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Autocomplete, Box, Button, Container, IconButton, Paper, TextField, Typography } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { User, useUser } from '../contexts/UserContext';
import { useAddressAutocomplete, useCityAutocomplete } from '../utils/AutoComplete';
import { UserSettings } from '@/types/types';

interface SettingsProps {
  onClose: () => void;
  settings: UserSettings;
  setSettings: React.Dispatch<React.SetStateAction<UserSettings>>;
}

export const Settings: React.FC<SettingsProps> = ({ onClose, settings, setSettings }) => {
  const { options, loading, fetchCities } = useCityAutocomplete();
  const { addressOptions, loadingAddress, fetchAddresses } = useAddressAutocomplete();
  const [isExpanded, setIsExpanded] = useState(false);
  const [allFieldsFilled, setAllFieldsFilled] = useState(false);
  const { user, setUser, addUserChangeListener } = useUser();

  useEffect(() => {
    addUserChangeListener((newUser) => {
      if (newUser) {
        setSettings(newUser.settings);
      }
    });
  }, [addUserChangeListener, setSettings]);

  useEffect(() => {
    const checkAllFieldsFilled = () => {
      const fields = [
        settings.preferredName,
        settings.cityState,
        settings.address,
        settings.aboutYou
      ];

      const areAllFilled = fields.every(field => field && field.trim() !== '');
      setAllFieldsFilled(areAllFilled);
    }
    checkAllFieldsFilled();
  }, [settings]);

  useEffect(() => {
    setSettings(user?.settings || {
      preferredName: '',
      cityState: '',
      address: '',
      aboutYou: ''
    });
  }, [setSettings, user?.settings]);

  const updateSettingsOnServer = async (newSettings: UserSettings) => {
    await axios.post(`${process.env.REACT_APP_API_URL}/user`,
      newSettings,
      {
        withCredentials: true,
        params: {
          forceUser: JSON.parse(process.env.REACT_APP_USER || '{}')?.email,
        }
      }
    )

    if (user) {
      const newUser: User = structuredClone(user);
      newUser.settings = newSettings;
      setUser(newUser);
    }
  };

  return (
    <div>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 2 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Settings
          </Typography>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}>
            {/* Settings content will go here */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3
            }}>
              <TextField
                id="preferred-name"
                label="Preferred Name"
                variant="outlined"
                fullWidth
                placeholder="Enter the name you'd like us to call you"
                size="medium"
                value={settings.preferredName}
                onChange={(e) => {
                  setSettings(prev => ({ ...prev, preferredName: e.target.value }));
                }}
                onBlur={() => updateSettingsOnServer(settings)}
              />

              <Autocomplete
                id="city-state"
                options={options}
                loading={loading}
                value={settings.cityState}
                onInputChange={(_, value) => {
                  setSettings(prev => ({ ...prev, cityState: value }));
                  fetchCities(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City, State"
                    variant="outlined"
                    placeholder="Start typing your city"
                    fullWidth
                  />
                )}
                onBlur={() => updateSettingsOnServer(settings)}
                freeSolo
                filterOptions={(x) => x}
              />

              <Autocomplete
                id="address"
                options={addressOptions}
                loading={loadingAddress}
                value={settings.address}
                onInputChange={(_, value) => {
                  setSettings(prev => ({ ...prev, address: value }));
                  fetchAddresses(value)
                }
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Address"
                    variant="outlined"
                    placeholder="Start typing your street address"
                    fullWidth
                  />
                )}
                onBlur={() => updateSettingsOnServer(settings)}
                freeSolo
                filterOptions={(x) => x}
              />

              <Box sx={{ position: 'relative' }}>
                <TextField
                  id="about-you"
                  label="About You (for Personalization)"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={isExpanded ? 20 : 5}
                  placeholder="Tell us about yourself! Share as much or as little as you'd like: your hobbies, interests, favorite topics, political leanings, or anything else you'd want your AI Assistant to know. This will help us personalize your news and content feeds just for you."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      overflowY: 'auto'
                    },
                    transition: 'all 0.3s ease'
                  }}
                  onBlur={() => updateSettingsOnServer(settings)}
                  onChange={(e) => {
                    setSettings(prev => ({ ...prev, aboutYou: e.target.value }));
                  }}
                  value={settings.aboutYou}
                />
                <IconButton
                  onClick={() => setIsExpanded(!isExpanded)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    bottom: 8,
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                  size="small"
                >
                  {isExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                </IconButton>
              </Box>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Your privacy matters to us. The information you share here is kept strictly confidential and is only used to personalize your experience within the app. We never share your data with third parties.
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  The more details you provide, the better we can tailor content and recommendations to your interests. This helps us create a more engaging and relevant experience just for you.
                </Typography>
              </Box>
              {!allFieldsFilled && (
                <div>
                  <Typography variant="h6" component="h2" gutterBottom>
                    All fields are required
                  </Typography>

                </div>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {allFieldsFilled && (
        <Button onClick={onClose}>Save</Button>
      )}
    </div>
  );
};